/**
 * Everybody on hold on the PBX: who, since when, held by whom — and a button to take them.
 *
 * Beside the waiting-for-a-car list and shaped like it, but a different list: these callers
 * are waiting for a person, not a car, and any dispatcher may take one back. Taking rings
 * the telephone the dispatcher answers on; the caller hears the firm's music until it is
 * answered, and is back on hold if it is not.
 *
 * Refetched rather than patched from the live message, for the same reason as the other two
 * queues: a client keeping its own copy of a list is a client that eventually disagrees with
 * the one the reminder reads.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import type { CallSummary } from '../calls/api';
import { ordersApi } from '../orders/api';
import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useDispatch } from '../shell/dispatchContext';
import { Banner, Button, Card, CardHeader, cx, Sub } from '../ui';
import { telephoneApi } from './api';

export function ParkedCalls({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { selectedOrder, setSelectedOrder } = useDispatch();

  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [taking, setTaking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The reference of the order each held caller gave, by order id.
   *
   * A caller on hold is somebody's order in the queue, and the number alone does not say
   * which; the reference does, and clicking the row opens it. Fetched per order rather
   * than carried on the call, because the call log does not know orders — the list is
   * two or three people at most, and the same fetch is what opening the order needs.
   */
  const [references, setReferences] = useState<Record<string, string>>({});

  useEffect(() => {
    const missing = calls
      .map((call) => call.orderId)
      .filter((id): id is string => id !== null && !(id in references));

    if (missing.length === 0) return;

    let cancelled = false;

    void Promise.all(
      missing.map((id) => ordersApi.get(id).then((order) => [id, order.reference] as const)),
    )
      .then((found) => {
        if (!cancelled) setReferences((known) => ({ ...known, ...Object.fromEntries(found) }));
      })
      .catch(() => {
        // A reference that will not load leaves the row as the number; the caller is
        // still taken back the same way.
      });

    return () => {
      cancelled = true;
    };
  }, [calls, references]);

  async function open(orderId: string) {
    try {
      setSelectedOrder(await ordersApi.get(orderId));
    } catch {
      // The order is gone or unreachable; nothing to show, and nothing to say over the list.
    }
  }

  // Ticks the "on hold for" column without re-fetching anything. Somebody who has been on
  // hold for two minutes is the most important row here, and a duration that only moved when
  // a message arrived would sit still for the whole wait.
  const [, tick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      setCalls(await telephoneApi.parked());
    } catch {
      // A card that will not load shows nothing. The console still takes orders.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const parked = await telephoneApi.parked();
        if (!cancelled) setCalls(parked);
      } catch {
        // As above.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (calls.length === 0) return;

    const timer = globalThis.setInterval(() => tick((n) => n + 1), 1000);

    return () => globalThis.clearInterval(timer);
  }, [calls.length]);

  useDispatcherHub({
    onCallParkedChanged: () => void refresh(),
    onCallFinished: () => void refresh(),
    onReconnected: () => void refresh(),
  });

  async function take(callId: string) {
    setTaking(callId);
    setError(null);

    try {
      await telephoneApi.pickup(callId);
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), { defaultValue: t('parked.couldNotTake') })
          : t('parked.couldNotTake'),
      );
    } finally {
      setTaking(null);
    }
  }

  // Nothing on hold and nothing to say. A card that is there and usually empty is a card
  // dispatchers stop seeing.
  if (calls.length === 0) return null;

  return (
    <Card className={className}>
      <CardHeader
        title={t('parked.title')}
        actions={<span className="text-secondary text-ink-3">{calls.length}</span>}
      />

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <ul className="m-0 p-0 list-none max-h-[320px] overflow-y-auto">
        {calls.map((call) => (
          <li
            key={call.id}
            className="flex items-center justify-between gap-2 px-3.5 py-2 border-b border-rule last:border-b-0"
          >
            {/* The caller's half of the row opens their order, and reads as selected
                while that order is the open one — so a dispatcher with three people
                waiting can see which order belongs to whom without leaving the list. A
                caller with no order yet is just a number, and not a button. */}
            <button
              type="button"
              disabled={call.orderId === null}
              aria-pressed={call.orderId !== null && selectedOrder?.id === call.orderId}
              onClick={() => call.orderId !== null && void open(call.orderId)}
              className={cx(
                'min-w-0 flex-1 text-left px-2 py-1 -mx-2 -my-1 rounded-control',
                call.orderId !== null && selectedOrder?.id === call.orderId
                  ? 'bg-surface-2'
                  : call.orderId !== null && 'hover:bg-surface-2',
              )}
            >
              <strong className="font-semibold tabular-nums">
                {call.from ?? t('calls.withheld')}
              </strong>
              {call.orderId !== null && references[call.orderId] !== undefined && (
                <span className="text-ink-3 font-mono tracking-[0.03em]">
                  {' · '}
                  {t('parked.order', { reference: references[call.orderId] })}
                </span>
              )}
              <Sub block>
                {t('parked.heldBy', {
                  name: call.parkedByName ?? t('parked.somebody'),
                  time: since(call.parkedAt),
                })}
              </Sub>
            </button>

            <Button
              size="sm"
              variant="outline"
              disabled={taking !== null}
              onClick={() => void take(call.id)}
            >
              {t('parked.take')}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** How long somebody has been on hold, as minutes and seconds. */
function since(parkedAt: string | null): string {
  if (parkedAt === null) return '—';

  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(parkedAt)) / 1000));

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
